/**
 * The privacy policy, at a public URL — https://fussilat.app/privacy.
 *
 * Both stores require one, and a reviewer opens it without signing in, so this
 * route sits outside the auth shell. It is written rather than generated: a
 * generated policy would describe a product this is not, and every clause here
 * matches something the code actually does — the retention sweep, the callable
 * that deletes a child, the recogniser's location.
 *
 * When the product changes, this changes with it. A policy that drifts from
 * the code is worse than none, because it is a promise nobody is keeping.
 */

import { PageMark } from '@/components/PageMark';

const UPDATED = '9 سبتمبر 2026';
const CONTACT = 'dr.abdulaziz35@gmail.com';

export default function PrivacyPage() {
  return (
    <main dir="rtl" className="mx-auto w-full max-w-3xl space-y-5 px-4 py-10 text-ink lg:py-14">
      <header className="space-y-3 pb-2">
        <h1 className="text-[28px] font-semibold tracking-tight">سياسة الخصوصية — فصّلت</h1>
        <p className="text-sm text-helper">آخر تحديث: {UPDATED}</p>
      </header>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">من نحن</h2>
        <p>
          فصّلت تطبيق تابع لجمعية فصّلت، لتصحيح تلاوة القرآن الكريم بين طالب ومعلم.
          يشرح هذا المستند ما نجمعه، ولماذا، وأين يُعالَج، وكيف يُحذف.
        </p>
      </section>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">ما نجمعه</h2>
        <ul className="list-disc space-y-1 pe-6">
          <li><b>الحساب:</b> رقم الجوال (للدخول برمز تحقق)، والاسم، والعمر، والجنس، والدولة والصف إن أُدخلا.</li>
          <li><b>حساب الضيف:</b> بلا رقم جوال — معرّف جهاز وسرّ يولّده جهازك. يُخزَّن كلاهما مُجزّأً (hash)، ولا يُخزَّن معرّف جهازك كما هو.</li>
          <li><b>سجل التلاوة:</b> مواضع ما تلوته (سورة وآية وكلمة)، والأخطاء التي وسمها معلمك، وحصصك وحضورك، ونقاطك وأوسمتك وشهاداتك. <b>لا نخزّن نص تلاوتك.</b></li>
          <li><b>التسجيلات الصوتية:</b> الكبسولة الصوتية التي يسجّلها المعلم للوالد بعد الحصة.</li>
          <li><b>صوت التدريب:</b> إن استخدمت التدريب الذاتي، يُرسل مقطعك للتعرف على الكلام ثم يُطرح فوراً.</li>
        </ul>
      </section>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">أين يُعالَج</h2>
        <ul className="list-disc space-y-1 pe-6">
          <li>الحسابات والسجلات والتسجيلات محفوظة في Google Firebase ضمن مشروع الجمعية، في أوروبا (europe-west1 / europe-west4).</li>
          <li>الصوت المباشر أثناء الحصة يمر عبر LiveKit ولا يُسجَّل.</li>
          <li>رموز التحقق تُرسل عبر مزوّد الرسائل Authentica.</li>
          <li>
            <b>صوت التدريب الذاتي</b> يُعالَج بخدمة Google Cloud Speech-to-Text. اللغة العربية غير
            متاحة في مراكز البيانات الأوروبية أو الخليجية لدى المزوّد، فتتم المعالجة في الولايات
            المتحدة. لا يُحفظ المقطع، ولا يُكتب نصّه في السجلات، ولا يُشارك مع طرف ثالث.
          </li>
        </ul>
      </section>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">الأطفال</h2>
        <ul className="list-disc space-y-1 pe-6">
          <li>لا يُقبل من هو دون العاشرة على أي نوع من الحسابات.</li>
          <li>حساب الطفل يملكه والده أو مدرسته، ويُسجَّل إقرار الموافقة مع الحساب.</li>
          <li>لكل تسجيل صوتي مدة احتفاظ محددة مسبقاً، ويُحذف تلقائياً عند انتهائها.</li>
          <li>يستطيع الوالد حذف حساب ابنه ومحتواه كاملاً من داخل التطبيق.</li>
        </ul>
      </section>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">ما لا نفعله</h2>
        <ul className="list-disc space-y-1 pe-6">
          <li>لا نبيع بياناتك ولا نؤجّرها، ولا نعرض إعلانات.</li>
          <li>لا نستخدم أدوات تتبّع أو تحليلات من طرف ثالث.</li>
          <li>لا نطالع سجل طالب إلا من له حق قراءته: الطالب نفسه، ووالده، ومعلمه، ومدرسته.</li>
        </ul>
      </section>

      <section className="card-soft space-y-2 p-6 leading-relaxed">
        <h2 className="text-lg font-semibold">حقوقك</h2>
        <p>
          لك أن تطلب نسخة من بياناتك أو حذفها. حذف الحساب يحذف سجل التلاوة والتسجيلات والشهادات
          المرتبطة به. للطلب أو لأي سؤال:{' '}
          <a className="text-primary underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>.
        </p>
        <p>
          نلتزم بنظام حماية البيانات الشخصية في المملكة العربية السعودية، وبلائحة GDPR لمستخدمينا
          في أوروبا.
        </p>
      </section>

      

      <section dir="ltr" className="card-soft space-y-3 p-6 text-left leading-relaxed">
        <h2 className="text-lg font-semibold">Privacy policy — Fussilat (English summary)</h2>
        <p className="text-sm">
          Fussilat is an app by جمعية فصّلت for correcting Qur'an recitation between a pupil and a
          teacher. We collect: your phone number (for the sign-in code), name, age, gender and
          optionally country and school grade; for guest accounts, no phone — only a hashed device
          identifier and a secret your device generates. We store the positions you recited (surah,
          ayah, word), mistakes your teacher marked, your sessions, points, badges and certificates.
          We do not store the text of your recitation.
        </p>
        <p className="text-sm">
          Accounts, records and voice notes live in Google Firebase inside the association's own
          project, in Europe. Live session audio passes through LiveKit and is not recorded.
          Sign-in codes are sent via Authentica. <b>Self-practice audio</b> is processed by Google
          Cloud Speech-to-Text; Arabic is not offered in the provider's European or Gulf regions,
          so that processing happens in the United States. The clip is not stored, its transcript
          is never written to logs, and it is not shared with any third party.
        </p>
        <p className="text-sm">
          Nobody under ten may hold an account of any kind. A child's account is owned by their
          parent or school, with consent recorded. Voice recordings have a set retention period and
          are deleted automatically. A parent can delete their child's account and all of its
          content from inside the app. We do not sell data, show ads, or use third-party analytics.
        </p>
        <p className="text-sm">
          For access or deletion requests:{' '}
          <a className="text-primary underline" href={`mailto:${CONTACT}`}>{CONTACT}</a>. We comply
          with Saudi Arabia's PDPL and with the GDPR for users in Europe.
        </p>
      </section>
    </main>
  );
}
